import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import { Collapse } from "bootstrap"

// Connects to data-controller="column-search-v3"
//
// Implements real-time search for V3 column library with auto-expanding groups
//
// Usage:
//   .search-box[
//     data-controller="column-search-v3"
//     data-action="input->column-search-v3#search"
//   ]
//
export default class extends Controller {
  static targets = [
    "searchInput",
    "container",
    "group",
    "item"
  ]

  connect() {
    logger.log('[Column Search V3] Connected', {
      groups: this.groupTargets.length,
      items: this.itemTargets.length
    })

    // Track original collapse state of each group
    this.originalCollapseStates = new Map()
  }

  search() {
    const searchTerm = this.searchInputTarget.value.toLowerCase().trim()

    // Split search term into individual words for word-order independent matching
    const searchWords = searchTerm.split(/\s+/).filter(w => w.length > 0)

    logger.log('[Column Search V3] Searching', { searchTerm, searchWords })

    // Capture original collapse state before first search
    if (searchWords.length > 0 && this.originalCollapseStates.size === 0) {
      this.captureCollapseStates()
    }

    let totalVisible = 0
    const groupMatches = {}
    const groupNameMatchedWords = new Map() // Track which words each group matches

    // First pass: Check which search words each group name matches
    this.groupTargets.forEach(group => {
      const groupNameElement = group.querySelector('.group-name')
      const groupName = groupNameElement?.textContent?.toLowerCase() || ''
      const collapseElement = group.querySelector('.collapse')
      const groupKey = collapseElement?.id || group?.id || 'unknown'

      // Find which search words match the group name
      const matchedWords = searchWords.filter(word => groupName.includes(word))

      if (matchedWords.length > 0) {
        groupNameMatchedWords.set(groupKey, matchedWords)
      }
    })

    // Filter each item with sophisticated multi-level matching
    this.itemTargets.forEach(item => {
      const itemName = item.dataset.columnSearchNameValue?.toLowerCase() || ''

      // Get the group this item belongs to
      const group = item.closest('[data-column-search-v3-target="group"]')
      const collapseElement = group?.querySelector('.collapse')
      const groupKey = collapseElement?.id || group?.id || 'unknown'

      if (searchWords.length === 0) {
        // No search - show everything
        item.classList.remove('d-none')
        this.clearHighlight(item)
        groupMatches[groupKey] = (groupMatches[groupKey] || 0) + 1
        totalVisible++
        return
      }

      // Get words that match the group name
      const groupMatchedWords = groupNameMatchedWords.get(groupKey) || []

      // Get words that need to be matched by the item itself
      const remainingWords = searchWords.filter(word => !groupMatchedWords.includes(word))

      // Item is visible if:
      // 1. Item name matches ALL remaining words (after group match), OR
      // 2. Item name matches ALL search words directly
      const itemMatchesRemainingWords = remainingWords.length === 0 || remainingWords.every(word =>
        itemName.includes(word)
      )
      const itemMatchesAllWords = searchWords.every(word => itemName.includes(word))

      const matchesSearch = itemMatchesRemainingWords || itemMatchesAllWords

      if (matchesSearch) {
        item.classList.remove('d-none')
        totalVisible++

        // Track this group has matches
        groupMatches[groupKey] = (groupMatches[groupKey] || 0) + 1

        // Highlight matching text in item
        // Highlight words that actually appear in the item name
        const wordsToHighlight = searchWords.filter(word => itemName.includes(word))
        if (wordsToHighlight.length > 0) {
          this.highlightText(item, wordsToHighlight)
        } else {
          this.clearHighlight(item)
        }
      } else {
        item.classList.add('d-none')
        this.clearHighlight(item)
      }
    })

    // Update group visibility and auto-expand groups with matches
    this.groupTargets.forEach(group => {
      const groupHeader = group.querySelector('.group-header')
      const groupCollapse = group.querySelector('.collapse')
      const groupNameElement = group.querySelector('.group-name')

      if (!groupHeader || !groupCollapse) return

      // Get collapse ID from button target
      const collapseId = groupHeader.getAttribute('data-bs-target')?.substring(1)
      const matchCount = groupMatches[collapseId] || 0
      const groupMatchedWords = groupNameMatchedWords.get(collapseId) || []

      const countBadge = group.querySelector('.group-count')
      const allItemsInGroup = group.querySelectorAll('[data-column-search-v3-target="item"]')

      if (searchTerm) {
        // When searching
        if (matchCount > 0) {
          // Show group with match count
          group.classList.remove('d-none')
          if (countBadge) {
            countBadge.textContent = matchCount
          }

          // Highlight group name if it has matched words
          if (groupMatchedWords.length > 0 && groupNameElement) {
            this.highlightGroupName(groupNameElement, groupMatchedWords)
          } else {
            this.clearGroupNameHighlight(groupNameElement)
          }

          // Auto-expand
          const bsCollapse = Collapse.getOrCreateInstance(groupCollapse, { toggle: false })
          bsCollapse.show()
        } else {
          // Hide group if no matches
          group.classList.add('d-none')
          this.clearGroupNameHighlight(groupNameElement)
        }
      } else {
        // When not searching, show all groups with original counts and restore collapse state
        group.classList.remove('d-none')
        if (countBadge) {
          countBadge.textContent = allItemsInGroup.length
        }
        this.clearGroupNameHighlight(groupNameElement)

        // Restore original collapse state
        this.restoreCollapseState(collapseId, groupCollapse)
      }
    })

    logger.log('[Column Search V3] Search complete', {
      totalVisible,
      groupsWithMatches: Object.keys(groupMatches).length,
      groupsWithNameMatches: groupNameMatchedWords.size
    })

    // Dispatch custom event for other controllers (e.g., column-selector-modal)
    this.element.dispatchEvent(new CustomEvent('column-search:complete', {
      bubbles: true,
      detail: {
        totalVisible,
        groupsWithMatches: Object.keys(groupMatches).length,
        searchTerm
      }
    }))
  }

  highlightText(item, searchWords) {
    const nameElement = item.querySelector('.column-name')
    if (!nameElement) return

    const originalText = nameElement.dataset.originalText || nameElement.textContent
    if (!nameElement.dataset.originalText) {
      nameElement.dataset.originalText = originalText
    }

    // Highlight each search word independently (supports word-order independent search)
    let highlightedText = originalText
    
    // Sort words by length (longest first) to avoid partial matches being highlighted first
    const sortedWords = [...searchWords].sort((a, b) => b.length - a.length)
    
    sortedWords.forEach(word => {
      // Escape special regex characters in the search word
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedWord})`, 'gi')
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>')
    })
    
    nameElement.innerHTML = highlightedText
  }

  clearHighlight(item) {
    const nameElement = item.querySelector('.column-name')
    if (!nameElement || !nameElement.dataset.originalText) return

    nameElement.textContent = nameElement.dataset.originalText
  }

  highlightGroupName(groupNameElement, searchWords) {
    if (!groupNameElement) return

    const originalText = groupNameElement.dataset.originalText || groupNameElement.textContent
    if (!groupNameElement.dataset.originalText) {
      groupNameElement.dataset.originalText = originalText
    }

    // Highlight each search word independently (supports word-order independent search)
    let highlightedText = originalText

    // Sort words by length (longest first) to avoid partial matches being highlighted first
    const sortedWords = [...searchWords].sort((a, b) => b.length - a.length)

    sortedWords.forEach(word => {
      // Escape special regex characters in the search word
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedWord})`, 'gi')
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>')
    })

    groupNameElement.innerHTML = highlightedText
  }

  clearGroupNameHighlight(groupNameElement) {
    if (!groupNameElement || !groupNameElement.dataset.originalText) return

    groupNameElement.textContent = groupNameElement.dataset.originalText
  }

  // Capture the current collapse state of all groups
  captureCollapseStates() {
    this.groupTargets.forEach(group => {
      const groupCollapse = group.querySelector('.collapse')
      const groupHeader = group.querySelector('.group-header')

      if (!groupCollapse || !groupHeader) return

      const collapseId = groupHeader.getAttribute('data-bs-target')?.substring(1)
      if (!collapseId) return

      // Store whether the group is currently expanded
      const isExpanded = groupCollapse.classList.contains('show')
      this.originalCollapseStates.set(collapseId, isExpanded)
    })

    logger.log('[Column Search V3] Captured collapse states', {
      count: this.originalCollapseStates.size,
      states: Object.fromEntries(this.originalCollapseStates)
    })
  }

  // Restore the original collapse state for a specific group
  restoreCollapseState(collapseId, groupCollapse) {
    if (!collapseId || !groupCollapse) return

    const wasExpanded = this.originalCollapseStates.get(collapseId)
    if (wasExpanded === undefined) return // No state captured yet

    const bsCollapse = Collapse.getOrCreateInstance(groupCollapse, { toggle: false })

    if (wasExpanded) {
      bsCollapse.show()
    } else {
      bsCollapse.hide()
    }
  }

  // Reset search
  reset() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
      this.search()
    }
  }
}

